import * as React from 'react';
import Hls, { type ErrorData, Events as HlsEvents } from 'hls.js';
import { AlertCircle, Clock, RefreshCw, ShieldCheck, Loader2, PlayCircle } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@cguard/design-system';

/**
 * Error envelope surfaced via {@link HevcVideoPlayerProps.onError}.
 *
 * `unsupported` is NOT an error from the V1 perspective. Retained H.265 recordings cannot play in Firefox or
 * Chrome-on-Linux, so the component surfaces a graceful empty-state rather
 * than a red overlay for that branch.
 */
export interface HevcVideoPlayerError {
  /**
   * Stable code for telemetry / branching:
   *  - `network` / `media` — fatal hls.js / native playback failures.
   *  - `unsupported` — browser does not advertise HEVC capability AND hls.js
   *    cannot drive it either. V1 trade-off — surface a calm message.
   *  - `other` — anything else (muxError, codec-string-zero-track, etc).
   */
  code: 'network' | 'media' | 'unsupported' | 'other';
  /** Human-readable detail (already i18n'd by the caller if needed). */
  message: string;
  /** Underlying hls.js payload, if any — useful for telemetry. */
  detail?: ErrorData;
}

/** Imperative playback controls for evidence workflows that seek by event timestamp. */
export interface HevcVideoPlayerHandle {
  /**
   * Seek to an offset from the start of the clip. Calls made before media
   * metadata loads are queued and applied once the duration is known.
   */
  seekTo: (offsetSeconds: number) => void;
  /** Current media time, or null while the video element is unavailable. */
  getCurrentTimeSeconds: () => number | null;
}

/**
 * Props for {@link HevcVideoPlayer}.
 */
export interface HevcVideoPlayerProps {
  /** Signed playback URL (already issued by `GET /v1/reviews/{id}/playback`). */
  playbackUrl: string;
  /** Codec reported by the retained clip metadata, for example `h264` or `h265`. */
  codec?: string;
  /** Human-readable label for the `<video>` accessible name + UI title. */
  reviewLabel: string;
  /**
   * Optional content-hash for chain-of-custody verification — when present,
   * a tooltip-revealing shield chip is rendered in the bottom-leading corner.
   * Hex string; first 8 chars are shown inline.
   */
  contentHashHex?: string;
  /**
   * Optional `expiresAtUtc` ISO timestamp from the playback endpoint. When
   * within 60s of expiry, the URL countdown switches to an amber tone AND
   * fires `onUrlNearExpiry` so the parent can refetch a fresh signed URL.
   */
  expiresAtUtc?: string;
  /**
   * Fired once when the playback URL is within 60s of expiry. The parent is
   * responsible for triggering a refetch of the signed URL — this hook only
   * raises the signal (debounced via internal state).
   */
  onUrlNearExpiry?: () => void;
  /** Optional className applied to the outer wrapper. */
  className?: string;
  /** Notified on every fatal error so the parent can surface a toast / metric. */
  onError?: (error: HevcVideoPlayerError) => void;
  /**
   * Optional clip-relative offset applied after media metadata loads. Values
   * below zero are clamped to zero; values past a finite duration are clamped
   * to the end of the clip.
   */
  initialSeekSeconds?: number;
  /** Reports the video's current clip-relative time during playback and seeks. */
  onCurrentTimeChange?: (currentTimeSeconds: number) => void;
  /** Optional translation map — keeps the component i18n-aware without an i18n peer dep. */
  labels?: Partial<{
    connecting: string;
    unsupportedTitle: string;
    unsupportedDescription: string;
    errorTitle: string;
    retry: string;
    chainOfCustody: string;
    urlExpiresIn: string;
    urlExpiresSeconds: string;
    playbackReady: string;
  }>;
  /** Hook for tests to force the hls.js path inside jsdom. */
  forceMse?: boolean;
  /** Hook for tests to force the "browser advertises HEVC support" probe outcome. */
  hevcSupportProbe?: () => 'native' | 'mse' | 'none';
}

const DEFAULT_LABELS: Required<NonNullable<HevcVideoPlayerProps['labels']>> = {
  connecting: 'Connecting…',
  unsupportedTitle: 'HEVC playback unavailable in this browser',
  unsupportedDescription:
    'Recorded clips are stored as H.265 (HEVC). Try Safari, or Chrome on macOS or Windows, to replay this segment.',
  errorTitle: 'Playback failed',
  retry: 'Retry',
  chainOfCustody: 'Chain-of-custody hash',
  urlExpiresIn: 'URL expires in',
  urlExpiresSeconds: 's',
  playbackReady: 'Ready to play',
};

/**
 * Probe browser HEVC capability.
 *
 * - `native` — `<video>` reports it can decode HEVC directly (Safari, Edge on
 *   newer Windows, Chrome on macOS/Windows with hardware HEVC). We prefer this
 *   path because it's the lowest-overhead route.
 * - `mse` — Media Source Extensions can drive HEVC (hls.js 1.5+ supports HEVC
 *   manifests when `MediaSource.isTypeSupported('video/mp4; codecs=hev1.x')`
 *   reports true). Used by Chrome-on-Windows + Chrome-on-macOS when the OS
 *   ships an HEVC decoder.
 * - `none` — neither path works. Surface the calm V1 trade-off message.
 *
 * The probe is run once per mount; we deliberately do not memoize across the
 * tree because the result depends on the `<video>` element type the consumer
 * just rendered (some test harnesses stub canPlayType).
 */
function defaultHevcSupportProbe(video: HTMLVideoElement | null): 'native' | 'mse' | 'none' {
  if (!video) return 'none';
  // RFC 6381 codec strings for HEVC Main / Main10 profiles. Browsers vary on
  // which they advertise — we try both common forms.
  const candidates = [
    'video/mp4; codecs="hev1.1.6.L93.B0"',
    'video/mp4; codecs="hvc1.1.6.L93.B0"',
    'application/vnd.apple.mpegurl; codecs="hvc1.1.6.L93.B0"',
  ];
  for (const c of candidates) {
    const verdict = video.canPlayType(c);
    if (verdict === 'probably' || verdict === 'maybe') return 'native';
  }

  // MSE path — hls.js can drive HEVC when MediaSource confirms support.
  const ms: typeof MediaSource | undefined =
    typeof MediaSource !== 'undefined' ? MediaSource : undefined;
  if (ms?.isTypeSupported?.('video/mp4; codecs="hev1.1.6.L93.B0"')) return 'mse';
  if (ms?.isTypeSupported?.('video/mp4; codecs="hvc1.1.6.L93.B0"')) return 'mse';

  return 'none';
}

type RecordingCodec = 'avc' | 'hevc' | 'unknown';

function recordingCodec(codec: string | undefined): RecordingCodec {
  const normalized =
    codec
      ?.trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]/g, '') ?? '';
  if (normalized === 'h264' || normalized === 'avc' || normalized === 'avc1') return 'avc';
  if (
    normalized === 'h265' ||
    normalized === 'hevc' ||
    normalized === 'hev1' ||
    normalized === 'hvc1'
  ) {
    return 'hevc';
  }
  return 'unknown';
}

type PlayerStatus = 'idle' | 'connecting' | 'playing' | 'unsupported' | 'error';

/**
 * `<HevcVideoPlayer/>` — cold playback tile for retained H.264 or H.265 clips.
 *
 * The clip codec is explicit when the evidence API reports it. H.264 uses the
 * browser-native MP4 path without an HEVC capability probe. H.265 retains the
 * capability check and calm unsupported state for browsers without a decoder.
 *
 * Polish features:
 *  - Subtle hairline + accent-tinted outer glow for depth without noise.
 *  - Tabular-numeral URL expiry countdown that flips amber under 60s and
 *    invokes `onUrlNearExpiry` for the parent to refetch.
 *  - Tooltip-revealing chain-of-custody hash chip in the bottom-leading corner.
 *  - Lucide icons throughout; no bespoke SVG.
 */
export const HevcVideoPlayer = React.forwardRef<HevcVideoPlayerHandle, HevcVideoPlayerProps>(
  HevcVideoPlayerImpl,
);

HevcVideoPlayer.displayName = 'HevcVideoPlayer';

function HevcVideoPlayerImpl(
  {
    playbackUrl,
    codec,
    reviewLabel,
    contentHashHex,
    expiresAtUtc,
    onUrlNearExpiry,
    className,
    onError,
    initialSeekSeconds,
    onCurrentTimeChange,
    labels,
    forceMse = false,
    hevcSupportProbe,
  }: HevcVideoPlayerProps,
  ref: React.ForwardedRef<HevcVideoPlayerHandle>,
): React.JSX.Element {
  const text = { ...DEFAULT_LABELS, ...labels };
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const metadataLoadedRef = React.useRef(false);
  const pendingSeekSecondsRef = React.useRef<number | null>(null);
  const initialSeekSecondsRef = React.useRef(initialSeekSeconds);
  const onCurrentTimeChangeRef = React.useRef(onCurrentTimeChange);
  onCurrentTimeChangeRef.current = onCurrentTimeChange;
  const [status, setStatus] = React.useState<PlayerStatus>('idle');
  const [errorState, setErrorState] = React.useState<HevcVideoPlayerError | null>(null);
  const [retryToken, setRetryToken] = React.useState(0);

  const applySeek = React.useCallback((video: HTMLVideoElement, offsetSeconds: number): void => {
    const boundedOffset = boundSeekSeconds(offsetSeconds, video.duration);
    if (boundedOffset === null) return;
    video.currentTime = boundedOffset;
    onCurrentTimeChangeRef.current?.(video.currentTime);
  }, []);

  const seekTo = React.useCallback(
    (offsetSeconds: number): void => {
      const normalisedOffset = normaliseSeekSeconds(offsetSeconds);
      if (normalisedOffset === null) return;

      const video = videoRef.current;
      if (!video || !metadataLoadedRef.current) {
        pendingSeekSecondsRef.current = normalisedOffset;
        return;
      }

      pendingSeekSecondsRef.current = null;
      applySeek(video, normalisedOffset);
    },
    [applySeek],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      seekTo,
      getCurrentTimeSeconds: () => videoRef.current?.currentTime ?? null,
    }),
    [seekTo],
  );

  React.useEffect(() => {
    initialSeekSecondsRef.current = initialSeekSeconds;
    if (initialSeekSeconds !== undefined) seekTo(initialSeekSeconds);
  }, [initialSeekSeconds, seekTo]);

  React.useEffect(() => {
    metadataLoadedRef.current = false;
    pendingSeekSecondsRef.current = normaliseSeekSeconds(initialSeekSecondsRef.current);
  }, [playbackUrl, retryToken]);

  const handleLoadedMetadata = React.useCallback((): void => {
    metadataLoadedRef.current = true;
    const pendingOffset = pendingSeekSecondsRef.current;
    if (pendingOffset === null) return;
    pendingSeekSecondsRef.current = null;

    const video = videoRef.current;
    if (video) applySeek(video, pendingOffset);
  }, [applySeek]);

  const handleTimeUpdate = React.useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>): void => {
      onCurrentTimeChangeRef.current?.(event.currentTarget.currentTime);
    },
    [],
  );

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setStatus('connecting');
    setErrorState(null);

    const declaredCodec = recordingCodec(codec);
    const isHlsManifest = /\.m3u8(\?|$)/i.test(playbackUrl);
    const support =
      declaredCodec === 'avc'
        ? 'native'
        : (hevcSupportProbe ?? (() => defaultHevcSupportProbe(video)))();

    const reportError = (err: HevcVideoPlayerError): void => {
      setErrorState(err);
      setStatus(err.code === 'unsupported' ? 'unsupported' : 'error');
      onError?.(err);
    };

    const attachNativePlayback = (failureMessage: string): (() => void) => {
      video.src = playbackUrl;
      const handleNativeError = (): void => {
        const err = video.error;
        reportError({
          code: 'media',
          message: err?.message ?? failureMessage,
        });
      };
      const handleCanPlay = (): void => setStatus('playing');
      video.addEventListener('error', handleNativeError);
      video.addEventListener('canplay', handleCanPlay);
      return (): void => {
        video.removeEventListener('error', handleNativeError);
        video.removeEventListener('canplay', handleCanPlay);
      };
    };

    if (!isHlsManifest && declaredCodec === 'avc') {
      return attachNativePlayback('H.264 playback failed.');
    }

    // Native HEVC path for Safari and supported Chrome/Edge installations.
    if (support === 'native' && declaredCodec !== 'avc' && !forceMse) {
      return attachNativePlayback('Native HEVC playback failed.');
    }

    // MSE / hls.js path for H.264 or supported HEVC manifests.
    if (
      isHlsManifest &&
      (declaredCodec === 'avc' || support === 'mse' || forceMse) &&
      Hls.isSupported()
    ) {
      const hls = new Hls({
        // Cold playback — long buffer is fine, this isn't live.
        backBufferLength: 60,
        // hls.js 1.5+ supports HEVC manifests. We deliberately do NOT filter
        // on `videoCodec` — HEVC is expected here (Tier-C recordings).
      });
      hls.attachMedia(video);
      hls.loadSource(playbackUrl);

      const handleManifestParsed = (): void => setStatus('playing');
      const handleError = (_event: typeof HlsEvents.ERROR, data: ErrorData): void => {
        if (!data.fatal) return;
        const code: HevcVideoPlayerError['code'] =
          data.type === 'networkError' ? 'network' : data.type === 'mediaError' ? 'media' : 'other';
        reportError({
          code,
          message: data.details || 'Stream playback failed.',
          detail: data,
        });
      };

      hls.on(HlsEvents.MANIFEST_PARSED, handleManifestParsed);
      hls.on(HlsEvents.ERROR, handleError);

      return (): void => {
        hls.off(HlsEvents.MANIFEST_PARSED, handleManifestParsed);
        hls.off(HlsEvents.ERROR, handleError);
        hls.destroy();
      };
    }

    if (isHlsManifest && declaredCodec === 'avc') {
      return attachNativePlayback('H.264 playback failed.');
    }

    // Direct MP4 path on browsers that decode HEVC natively.
    if (!isHlsManifest && support !== 'none') {
      return attachNativePlayback('HEVC playback failed.');
    }

    // ── Unsupported — graceful empty-state ──────────────────────────────────
    reportError({
      code: 'unsupported',
      message: text.unsupportedDescription,
    });
    return undefined;
    // We intentionally omit `text` from deps — it's reconstructed on every
    // render but only `unsupportedDescription` flows in here, and the message
    // is only read inside the unsupported branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackUrl, codec, retryToken, forceMse, hevcSupportProbe, onError]);

  const handleRetry = React.useCallback(() => {
    setRetryToken((t) => t + 1);
  }, []);

  // ── URL-expiry countdown ──────────────────────────────────────────────────
  const [secondsToExpiry, setSecondsToExpiry] = React.useState<number | null>(null);
  const nearExpiryFiredRef = React.useRef(false);

  React.useEffect(() => {
    nearExpiryFiredRef.current = false;
    if (!expiresAtUtc) {
      setSecondsToExpiry(null);
      return;
    }
    const expiryMs = new Date(expiresAtUtc).getTime();
    if (Number.isNaN(expiryMs)) {
      setSecondsToExpiry(null);
      return;
    }
    const tick = (): void => {
      const remaining = Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
      setSecondsToExpiry(remaining);
      if (remaining <= 60 && remaining > 0 && !nearExpiryFiredRef.current) {
        nearExpiryFiredRef.current = true;
        onUrlNearExpiry?.();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAtUtc, onUrlNearExpiry]);

  const expirySoon = secondsToExpiry !== null && secondsToExpiry <= 60;

  return (
    <TooltipProvider>
      <div
        className={cn(
          // Outer frame — hairline + faint accent-tinted glow for depth.
          'group relative isolate overflow-hidden rounded-md',
          'border border-[var(--color-hairline)]',
          'bg-[var(--color-surface-sunk)]',
          'shadow-[0_0_0_1px_color-mix(in_oklch,var(--color-accent)_8%,transparent)]',
          'transition-shadow duration-200',
          className,
        )}
        data-testid="hevc-video-player"
      >
        <video
          ref={videoRef}
          aria-label={reviewLabel}
          className={cn(
            'block aspect-video h-auto w-full bg-[var(--color-surface-sunk)]',
            'transition-opacity duration-200 ease-out',
            status === 'playing' ? 'opacity-100' : 'opacity-0',
          )}
          controls
          autoPlay
          muted
          playsInline
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
        />

        {/* ── Connecting overlay (frosted glass) ──────────────────────────── */}
        {status === 'connecting' && (
          <div
            role="status"
            aria-live="polite"
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center gap-2.5',
              'bg-[var(--color-surface-sunk)]/55 backdrop-blur-[2px]',
              'text-[var(--color-foreground)]',
            )}
            data-testid="hevc-video-player-connecting"
          >
            <Loader2 className="size-5 animate-spin text-[var(--color-accent)]" aria-hidden />
            <p className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-[var(--color-foreground-2)]">
              {text.connecting}
            </p>
          </div>
        )}

        {/* ── Unsupported overlay (calm, not red) ─────────────────────────── */}
        {status === 'unsupported' && errorState && (
          <div
            role="status"
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center',
              'bg-[var(--color-surface-sunk)]/85 backdrop-blur-[2px]',
              'text-[var(--color-foreground)]',
            )}
            data-testid="hevc-video-player-unsupported"
          >
            <div
              className={cn(
                'flex size-12 items-center justify-center rounded-full',
                'border border-[var(--color-hairline)] bg-[var(--color-surface-3)] text-[var(--color-accent)]',
              )}
            >
              <PlayCircle className="size-6" aria-hidden />
            </div>
            <div className="space-y-1.5 max-w-sm">
              <p className="text-sm font-medium text-[var(--color-foreground)]">
                {text.unsupportedTitle}
              </p>
              <p className="text-xs leading-relaxed text-[var(--color-foreground-3)]">
                {text.unsupportedDescription}
              </p>
            </div>
          </div>
        )}

        {/* ── Error overlay (distinct from unsupported) ──────────────────── */}
        {status === 'error' && errorState && (
          <div
            role="alert"
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center',
              'bg-[var(--color-surface-sunk)]/95 backdrop-blur-[2px]',
              'text-[var(--color-danger)]',
            )}
            data-testid="hevc-video-player-error"
          >
            <div
              className={cn(
                'flex size-12 items-center justify-center rounded-full',
                'border border-[var(--color-danger)]/40 bg-[var(--color-danger-tint)]',
                'text-[var(--color-danger)]',
              )}
            >
              <AlertCircle className="size-6" aria-hidden />
            </div>
            <div className="space-y-1.5 max-w-sm">
              <p className="text-sm font-medium">{text.errorTitle}</p>
              <p className="text-xs leading-relaxed text-[var(--color-danger)]/85">
                {errorState.message}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              className={cn(
                'border-[var(--color-danger)]/40 bg-[var(--color-surface-3)] text-[var(--color-foreground)]',
                'hover:bg-[var(--color-danger-tint)] hover:text-[var(--color-foreground)]',
              )}
            >
              <RefreshCw className="size-3.5" aria-hidden />
              {text.retry}
            </Button>
          </div>
        )}

        {/* ── Bottom strip: chain-of-custody chip (leading) + expiry (trailing) ── */}
        {(contentHashHex || secondsToExpiry !== null) && (
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2',
              'border-t border-[var(--color-hairline)] bg-[var(--color-surface-sunk)]/85 px-3 py-2',
            )}
          >
            <div className="pointer-events-auto flex items-center gap-1.5">
              {contentHashHex && (
                <Tooltip>
                  <TooltipTrigger
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-sm',
                      'border border-[var(--color-hairline)] bg-[var(--color-surface-3)] backdrop-blur-sm',
                      'ps-1.5 pe-2 py-1',
                      'font-mono text-[0.6875rem] tabular-nums text-[var(--color-foreground-2)]',
                      'hover:border-[var(--color-accent)]/60 hover:text-[var(--color-foreground)]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
                      'transition-colors',
                    )}
                    aria-label={text.chainOfCustody}
                  >
                    <ShieldCheck className="size-3.5 text-[var(--color-accent)]" aria-hidden />
                    <span>
                      {text.chainOfCustody} · {contentHashHex.slice(0, 8)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-medium">{text.chainOfCustody}</p>
                      <p className="break-all font-mono text-[0.65rem] tabular-nums opacity-80">
                        {contentHashHex}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {secondsToExpiry !== null && (
              <div className="pointer-events-auto">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-sm',
                    'border bg-[var(--color-surface-3)] backdrop-blur-sm',
                    'ps-1.5 pe-2 py-1',
                    'font-mono text-[0.6875rem] tabular-nums',
                    'transition-colors',
                    expirySoon
                      ? 'border-[var(--color-warning)]/60 text-[var(--color-warning)]'
                      : 'border-[var(--color-hairline)] text-[var(--color-foreground-3)]',
                  )}
                  data-testid="hevc-video-player-expiry"
                  data-expiry-soon={expirySoon ? 'true' : 'false'}
                >
                  <Clock className="size-3.5" aria-hidden />
                  <span>
                    {text.urlExpiresIn} · {secondsToExpiry}
                    {text.urlExpiresSeconds}
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

function normaliseSeekSeconds(offsetSeconds: number | undefined): number | null {
  if (offsetSeconds === undefined || !Number.isFinite(offsetSeconds)) return null;
  return Math.max(0, offsetSeconds);
}

function boundSeekSeconds(offsetSeconds: number, durationSeconds: number): number | null {
  const normalisedOffset = normaliseSeekSeconds(offsetSeconds);
  if (normalisedOffset === null) return null;
  return Number.isFinite(durationSeconds) && durationSeconds >= 0
    ? Math.min(normalisedOffset, durationSeconds)
    : normalisedOffset;
}
